import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useRef, useState, type ReactNode } from 'react';

import { ApiError } from '@/api/envelope';
import type {
  ActorKind,
  AuditAction,
  AuditActor,
  AuditChange,
  AuditEntry,
} from '@/api/generated';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { FilterBar, type DateRange } from '@/components/ui/FilterBar';
import { LiveRegion } from '@/components/ui/LiveRegion';

import { ACTION_CODES, actionPhrase } from './actions';
import { formatMoment, lastThirtyDays } from './dates';
import { ActivityDetailDrawer } from './ActivityDetailDrawer';
import { summarizeEntry } from './summary';
import { ACTOR_KIND_LABELS, actorLabel, organizationLabel } from './words';

/** The server's own page size. */
export const ACTIVITY_PAGE_SIZE = 20;

/** Everything a feed request carries. Empty values are left off. */
export interface ActivityQuery {
  from?: string;
  to?: string;
  action?: AuditAction;
  actor_kind?: ActorKind;
  actor_id?: string;
  page: number;
  limit: number;
  /** Anything the page adds - the staff view's organization. */
  [extra: string]: string | number | undefined;
}

/** What an actors request carries: the feed's filters minus the person and paging. */
export type ActorsQuery = Pick<ActivityQuery, 'from' | 'to' | 'action'> &
  Record<string, string | undefined>;

export interface ActivityPage {
  items: AuditEntry[];
  total: number;
}

interface ActivityTableProps {
  /** Which view this is, so the two never share a cache entry. */
  view: string;
  fetchEntries: (query: ActivityQuery) => Promise<ActivityPage>;
  fetchActors: (query: ActorsQuery) => Promise<AuditActor[]>;
  /** A caller's own filter controls, drawn in the filter bar. */
  extraFilters?: ReactNode;
  /** The values those controls hold, sent with every request. Empty means any. */
  extraValues?: Record<string, string>;
  /** Called by Clear filters, so the caller clears its own controls too. */
  onClearExtra?: () => void;
  /** Adds the organization column and the drawer's organization line. */
  showOrganization?: boolean;
  /** Fields this viewer may not be shown; their changes are dropped entirely. */
  hiddenFields?: ReadonlySet<string>;
}

interface Filters {
  range: DateRange;
  action: string;
  /** Which kind of person acted: staff, organization users or the system. */
  kind: string;
  actor: string;
  page: number;
}

/** The Done by choices, in the order the filter lists them. */
const KIND_OPTIONS: { value: ActorKind; label: string }[] = [
  { value: 'ACTIVIST_STAFF', label: 'Activist staff' },
  { value: 'ORGANIZATION_USER', label: 'Organization users' },
  { value: 'SYSTEM', label: 'System' },
];

/** The query object with every empty value left out, as the server wants it. */
function compact<T extends Record<string, unknown>>(values: T): T {
  return Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== '' && value !== undefined),
  ) as T;
}

/**
 * The audit log as a table: when, who, what, and to what, newest first, with
 * filters that are the server's own and a drawer for the whole entry.
 *
 * Shared by the Super Admin's Activity page and the staff one. They differ in
 * which feed they read and in the staff view's organization filter and column,
 * which come in through the props.
 *
 * Every filter is sent to the server rather than applied here, so a page is a
 * page of what matched. One object holds them all and is the query key, and
 * any change to it goes back to page one.
 *
 * Nothing on the table or the drawer changes an entry. There is no control for
 * it, because the log records what happened.
 */
export function ActivityTable({
  view,
  fetchEntries,
  fetchActors,
  extraFilters,
  extraValues = {},
  onClearExtra,
  showOrganization = false,
  hiddenFields,
}: ActivityTableProps) {
  const [filters, setFilters] = useState<Filters>(() => ({
    range: lastThirtyDays(),
    action: '',
    kind: '',
    actor: '',
    page: 1,
  }));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const openers = useRef(new Map<string, HTMLButtonElement>());

  /*
   * A change to the caller's own filters goes back to page one, like any other
   * filter. Adjusted while rendering, compared by value, so the request for a
   * page that no longer exists is never made.
   */
  const extraKey = JSON.stringify(extraValues);
  const [seenExtra, setSeenExtra] = useState(extraKey);
  if (extraKey !== seenExtra) {
    setSeenExtra(extraKey);
    setFilters((prev) => ({ ...prev, page: 1 }));
  }

  const query: ActivityQuery = compact({
    from: filters.range.from,
    to: filters.range.to,
    action: filters.action as AuditAction,
    actor_kind: filters.kind as ActorKind,
    actor_id: filters.actor,
    ...extraValues,
    page: filters.page,
    limit: ACTIVITY_PAGE_SIZE,
  });

  const actorsQuery: ActorsQuery = compact({
    from: filters.range.from,
    to: filters.range.to,
    action: filters.action as AuditAction,
    ...extraValues,
  });

  const entries = useQuery({
    queryKey: ['activity', view, query],
    queryFn: () => fetchEntries(query),
    placeholderData: keepPreviousData,
  });

  // Refetched whenever the range or the action changes, so the person list
  // never offers somebody with nothing to show in what is being looked at.
  const actors = useQuery({
    queryKey: ['activity', view, 'actors', actorsQuery],
    queryFn: () => fetchActors(actorsQuery),
    placeholderData: keepPreviousData,
  });

  /** Changes anything that narrows the list, and returns to page one. */
  const narrow = (change: Partial<Filters>) =>
    setFilters((prev) => ({ ...prev, ...change, page: 1 }));

  const clear = () => {
    // No dates at all, not the thirty-day default: clearing means everything.
    narrow({ range: { from: '', to: '' }, action: '', kind: '', actor: '' });
    onClearExtra?.();
  };

  /** An entry's changes, less anything this viewer may not see. */
  const visibleChanges = (entry: AuditEntry): AuditChange[] =>
    (entry.changes ?? []).filter((change) => !hiddenFields?.has(change.field));

  const rows = entries.data?.items ?? [];
  const total = entries.data?.total ?? 0;
  const selectedIndex = rows.findIndex((row) => row.id === selectedId);
  const selected = selectedIndex >= 0 ? rows[selectedIndex] : null;

  function close() {
    const id = selectedId;
    setSelectedId(null);
    // Back to the row of the entry last shown, which after Newer or Older is
    // not the one that opened the drawer.
    if (id) requestAnimationFrame(() => openers.current.get(id)?.focus());
  }

  // The actors endpoint takes no kind, so the list is narrowed here to the
  // kind chosen under Done by. The system is never a person to pick.
  const people = (actors.data ?? []).filter(
    (actor) =>
      actor.kind !== 'SYSTEM' &&
      actor.id &&
      (!filters.kind || actor.kind === filters.kind),
  );

  /** Done by changed: a chosen person of another kind would match nothing. */
  const chooseKind = (kind: string) => {
    const chosen = (actors.data ?? []).find((actor) => actor.id === filters.actor);
    const keepsActor = !kind || !chosen || chosen.kind === kind;
    narrow({ kind, ...(keepsActor ? {} : { actor: '' }) });
  };

  /*
   * The sentence first, so it is what a narrow screen shows before the table
   * scrolls. It is also the row's one focusable control: a click anywhere on
   * the row opens the entry, and this is the same thing by keyboard, named by
   * its own words so a voice user can say what they see.
   */
  const columns: Column<AuditEntry>[] = [
    {
      key: 'what',
      header: 'What happened',
      render: (row) => (
        <ButtonBase
          ref={(node: HTMLButtonElement | null) => {
            if (node) openers.current.set(row.id, node);
            else openers.current.delete(row.id);
          }}
          aria-haspopup="dialog"
          onClick={() => setSelectedId(row.id)}
          sx={{
            font: 'inherit',
            color: 'inherit',
            textAlign: 'left',
            justifyContent: 'flex-start',
            borderRadius: 0.5,
            '&.Mui-focusVisible, &:focus-visible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 2,
            },
          }}
        >
          {summarizeEntry(row, visibleChanges(row))}
        </ButtonBase>
      ),
    },
    {
      key: 'who',
      header: 'Who',
      // The kind in words under the name, not in a tooltip that keyboard,
      // touch and screen-reader users never get.
      render: (row) => (
        <>
          <Box component="span" sx={{ display: 'block' }}>
            {actorLabel(row.actor)}
          </Box>
          {row.actor.kind !== 'SYSTEM' ? (
            <Box
              component="span"
              sx={{ display: 'block', color: 'text.secondary', fontSize: '0.8125rem' }}
            >
              {ACTOR_KIND_LABELS[row.actor.kind]}
            </Box>
          ) : null}
        </>
      ),
    },
    { key: 'when', header: 'When', render: (row) => formatMoment(row.occurred_at) },
    ...(showOrganization
      ? [
          {
            key: 'organization',
            header: 'Organization',
            render: (row: AuditEntry) => organizationLabel(row),
          },
        ]
      : []),
  ];

  const filtered =
    Boolean(
      filters.range.from ||
      filters.range.to ||
      filters.action ||
      filters.kind ||
      filters.actor,
    ) || Object.values(extraValues).some(Boolean);

  const failure = entries.isError
    ? ((entries.error instanceof ApiError ? entries.error.serverMessage : null) ??
      'The activity could not be loaded.')
    : null;

  return (
    <Box>
      <FilterBar
        dateRange={filters.range}
        onDateRangeChange={(range) => narrow({ range })}
        filters={[
          {
            name: 'action',
            label: 'Action',
            options: ACTION_CODES.map((code) => ({
              value: code,
              label: actionPhrase(code),
            })),
          },
          {
            name: 'kind',
            label: 'Done by',
            options: KIND_OPTIONS,
          },
          {
            name: 'actor',
            label: 'Person',
            options: people.map((actor) => ({
              value: actor.id!,
              label: `${actorLabel(actor)} (${ACTOR_KIND_LABELS[actor.kind]})`,
            })),
          },
        ]}
        values={{ action: filters.action, kind: filters.kind, actor: filters.actor }}
        onFilterChange={(name, value) =>
          name === 'kind' ? chooseKind(value) : narrow({ [name]: value })
        }
        extra={extraFilters}
        extraActive={Object.values(extraValues).some(Boolean)}
        onClear={clear}
      />

      <DataTable
        caption="Activity"
        columns={columns}
        rows={rows}
        getRowKey={(row) => row.id}
        onRowClick={(row) => setSelectedId(row.id)}
        loading={entries.isPending}
        error={failure}
        onRetry={() => void entries.refetch()}
        emptyMessage="Nothing has been recorded yet."
        noMatchMessage="No activity matches these filters."
        filtered={filtered}
        refreshing={entries.isPlaceholderData}
        page={filters.page}
        pageSize={ACTIVITY_PAGE_SIZE}
        total={total}
        onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
      />

      {/* A filter change replaces the rows without moving focus, so the new
          count is announced; the table announces a page change itself. */}
      <LiveRegion
        message={
          entries.isSuccess && !entries.isPlaceholderData
            ? `${total} ${total === 1 ? 'entry' : 'entries'} found.`
            : ''
        }
      />

      <ActivityDetailDrawer
        entry={selected}
        changes={selected ? visibleChanges(selected) : []}
        showOrganization={showOrganization}
        onClose={close}
        onNewer={
          selectedIndex > 0 ? () => setSelectedId(rows[selectedIndex - 1].id) : undefined
        }
        onOlder={
          selectedIndex >= 0 && selectedIndex < rows.length - 1
            ? () => setSelectedId(rows[selectedIndex + 1].id)
            : undefined
        }
      />
    </Box>
  );
}
